import React, { lazy, Suspense, useContext, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { AddIcon, DownloadIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { StateContainer } from '@/components/ui/StateContainer'
import { createRepairOrderExport, getRepairOrderExport } from '@/services/dealer.service'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import AddRepairOrderModal from './AddRepairOrderModal'
import {
  INTAKE_STATUSES,
  ACTIVE_STATUSES,
  RESOLVED_STATUSES,
  INTAKE_COLUMNS,
  ACTIVE_COLUMNS,
  RESOLVED_INTAKE_COLUMNS,
} from './constants'
import IntakeKpis from './IntakeKpis'
import { IntakeTableSection } from './IntakeTableSection'
import SetStatusModal from './SetStatusModal'

const AdvancedKpiPanel = lazy(() => import('./AdvancedKpiPanel'))

const RoIntake = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { dealer, error } = useOutletContext()
  const { createNewTask } = useContext(TaskStoreContext)
  const { isReadOnlySession, isDealerUser, isDealerAdmin, isImpersonating } =
    useContext(UserStoreContext)

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [selectedRepairOrder, setSelectedRepairOrder] = useState(null)
  const [loadingExport, setLoadingExport] = useState(false)
  const [showAdvancedKpis, setShowAdvancedKpis] = useState(false)

  const handleErrors = (message) => toast(message, 'error')

  const handleExport = async () => {
    const exportTask = await createRepairOrderExport(
      `/dealers/${id}/repair-order-exports/`,
      handleErrors,
      setLoadingExport,
      { exportType: 'intake' },
    )

    if (exportTask) {
      createNewTask('Download', exportTask, {
        label: 'RO Intake',
        filename: `${dealer?.name || 'Dealer'} - RO Intake.xlsx`,
        checkUpdatedTask: async () => {
          const updatedTask = await getRepairOrderExport(id, exportTask.id, handleErrors)
          return updatedTask
        },
      })
    }
  }

  const kpiRef = useRef()
  const intakeRef = useRef()
  const activeRef = useRef()
  const resolvedRef = useRef()

  // Redirect if dealer is loaded but RO Intake is not enabled
  useEffect(() => {
    if (dealer && !dealer.isRoIntakeEnabled) {
      navigate(`/dealer/${id}/detail`, { replace: true })
    }
  }, [dealer])

  const handleRowClicked = (row) => {
    setSelectedRepairOrder(row)
    setIsStatusModalOpen(true)
  }

  const refreshAll = () => {
    kpiRef.current?.refresh()
    intakeRef.current?.refresh()
    activeRef.current?.refresh()
    resolvedRef.current?.refresh()
  }

  return (
    <>
      <Helmet>
        <title>{dealer?.name || 'RO Intake'}</title>
      </Helmet>

      <StateContainer error={error}>
        <div className="flex w-full flex-col space-y-4">
          {showAdvancedKpis ? (
            <Suspense
              fallback={
                <div className="text-charcoal-400 p-6 text-center text-sm">Loading...</div>
              }
            >
              <AdvancedKpiPanel dealerId={id} onBack={() => setShowAdvancedKpis(false)} />
            </Suspense>
          ) : (
            <IntakeKpis
              ref={kpiRef}
              dealerId={id}
              onViewAdvanced={() => setShowAdvancedKpis(true)}
            />
          )}

          <IntakeTableSection
            ref={intakeRef}
            dealerId={id}
            title="Intake"
            statusValues={INTAKE_STATUSES}
            extraParams="include_ol_state=Draft"
            columns={INTAKE_COLUMNS}
            defaultOpen
            defaultSort="received_at"
            onRowClicked={handleRowClicked}
            toolbarActions={
              <div className="flex items-center gap-2">
                <Button
                  className="px-3 py-2"
                  icon={<AddIcon className="h-4 stroke-[#ffffff]" />}
                  label="Add Repair Order"
                  onClick={() => setIsAddModalOpen(true)}
                  disabled={isReadOnlySession}
                  ariaLabel="Add Repair Order"
                />
                <Button
                  className="px-1 py-2"
                  label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
                  ariaLabel="Export RO Intake"
                  onClick={handleExport}
                  disabled={loadingExport || isImpersonating}
                />
              </div>
            }
          />

          <IntakeTableSection
            ref={activeRef}
            dealerId={id}
            title="Active"
            statusValues={ACTIVE_STATUSES}
            extraParams="exclude_ol_state=Draft"
            columns={ACTIVE_COLUMNS}
            defaultOpen
            defaultSort="modified_at"
            onRowClicked={handleRowClicked}
            showDealerClosedFilter
          />

          <IntakeTableSection
            ref={resolvedRef}
            dealerId={id}
            title="Resolved"
            statusValues={RESOLVED_STATUSES}
            columns={RESOLVED_INTAKE_COLUMNS}
            defaultOpen
            defaultSort="-modified_at"
            onRowClicked={handleRowClicked}
            showDealerClosedFilter
          />
        </div>
      </StateContainer>

      <AddRepairOrderModal
        open={isAddModalOpen}
        setOpen={setIsAddModalOpen}
        dealerId={id}
        onSuccess={refreshAll}
      />

      <SetStatusModal
        open={isStatusModalOpen}
        setOpen={setIsStatusModalOpen}
        repairOrder={selectedRepairOrder}
        dealerId={id}
        dealer={dealer}
        isDealerUser={isDealerUser}
        isDealerAdmin={isDealerAdmin}
        onSuccess={refreshAll}
      />
    </>
  )
}

export default RoIntake
